import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import LoadingSplash from './LoadingSplash';

const PageTransition = ({ children }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState('Loading...');

  // Route-specific loading messages
  const getLoadingMessage = (pathname) => {
    const messages = {
      '/daily-journal-entry': 'Preparing your journal...',
      '/dashboard-overview': 'Loading insights...',
      '/analytics-dashboard': 'Analyzing your progress...',
      '/child-profile-management': 'Loading profiles...',
      '/journal-history': 'Fetching your entries...',
      '/subscription-checkout': 'Setting up checkout...',
      '/admin-dashboard': 'Loading admin panel...'
    };
    return messages?.[pathname] || 'Loading page...';
  };

  React.useEffect(() => {
    setIsLoading(true);
    setLoadingMessage(getLoadingMessage(location?.pathname));
    
    // Simulate loading time for better UX
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300); // Short delay to show transition

    return () => clearTimeout(timer);
  }, [location?.pathname]);

  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20,
      scale: 0.98
    },
    in: {
      opacity: 1,
      x: 0,
      scale: 1
    },
    out: {
      opacity: 0,
      x: -20,
      scale: 1.02
    }
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.4
  };

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <LoadingSplash 
            message={loadingMessage}
            icon="Loader2"
            fullScreen={false}
          />
        </motion.div>
      ) : (
        <motion.div
          key={location?.pathname}
          initial="initial"
          animate="in"
          exit="out"
          variants={pageVariants}
          transition={pageTransition}
          className="w-full"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageTransition;